// src/components/SynergyDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Bar,
} from "recharts";
import { CalendarDays } from "lucide-react";

import DashboardLayout from "./DashboardLayout";
import { apiFetch } from "../utils/apiClient";

const COINS_PER_USD = 2000;
const ITO_PRICE_PER_COIN = 0.005;
const PACKAGE_BONUS_RULES = [
  { minAmount: 500, bonusCoins: 100000 },
  { minAmount: 200, bonusCoins: 50000 },
];

const CHART_RANGE_OPTIONS = [
  { value: "7d", label: "Last 7 days", days: 7 },
  // { value: "15d", label: "Last 15 days", days: 15 },
  { value: "1m", label: "Last 1 month", months: 1 },
  { value: "2m", label: "Last 2 months", months: 2 },
  { value: "3m", label: "Last 3 months", months: 3 },
  { value: "6m", label: "Last 6 months", months: 6 },
  // { value: "1y", label: "Last 1 year", months: 12 },
];

const getReferralDiscount = (transaction) => {
  const discount = transaction?.metadata?.referralDiscount;
  if (typeof discount === "number") return discount;
  const parsed = Number(discount);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeAmountInput = (value) => {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string") {
    const cleaned = value.replace(/[^0-9.]/g, "");
    if (!cleaned) {
      return Number.NaN;
    }
    return Number.parseFloat(cleaned);
  }
  return Number.NaN;
};

const resolveBonusFromAmount = (value) => {
  const normalizedAmount = normalizeAmountInput(value);
  if (!Number.isFinite(normalizedAmount) || normalizedAmount <= 0) {
    return 0;
  }

  for (const rule of PACKAGE_BONUS_RULES) {
    if (normalizedAmount >= rule.minAmount) {
      return rule.bonusCoins;
    }
  }

  return 0;
};

const getTransactionOriginalAmount = (transaction) => {
  if (!transaction) {
    return 0;
  }

  const metaAmount = normalizeAmountInput(transaction?.metadata?.originalAmount);
  if (Number.isFinite(metaAmount) && metaAmount > 0) {
    return metaAmount;
  }

  const recordedAmount = normalizeAmountInput(transaction?.amount);
  return Number.isFinite(recordedAmount) ? recordedAmount : 0;
};

const getTransactionBonusCoins = (transaction) => {
  const baseAmount = getTransactionOriginalAmount(transaction);
  return resolveBonusFromAmount(baseAmount);
};

export default function SynergyDashboard({ user, onSignOut }) {
  const [transactions, setTransactions] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [statsError, setStatsError] = useState("");
  const [chartRange, setChartRange] = useState(CHART_RANGE_OPTIONS[2].value);
  const rewardCoins = useMemo(() => {
    const parsed = Number(user?.rewardCoins);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return 0;
    }
    return parsed;
  }, [user?.rewardCoins]);

  useEffect(() => {
    let ignore = false;

    const loadTransactions = async () => {
      try {
        const response = await apiFetch("/transactions");
        if (!ignore && response?.transactions) {
          setTransactions(response.transactions);
        }
      } catch (error) {
        if (!ignore) {
          setStatsError(
            error?.data?.message || error.message || "Failed to load portfolio.",
          );
        }
      } finally {
        if (!ignore) {
          setLoadingStats(false);
        }
      }
    };

    loadTransactions();

    return () => {
      ignore = true;
    };
  }, []);

  const totalPurchased = useMemo(
    () =>
      transactions.reduce(
        (accumulator, transaction) =>
          accumulator + getTransactionOriginalAmount(transaction),
        0,
      ),
    [transactions],
  );

  const totalDiscountEarned = useMemo(() => {
    // 1. Sum of referral discounts from transactions
    const transactionDiscounts = transactions.reduce((accumulator, transaction) => {
      const discountValue =
        typeof transaction.metadata?.referralDiscount === "number"
          ? transaction.metadata.referralDiscount
          : Number(transaction.metadata?.referralDiscount);
      return accumulator + (Number.isFinite(discountValue) ? discountValue : 0);
    }, 0);

    // 2. Convert referral coins (rewardCoins) to USD value and add to discounts
    const referralCoinsValue = rewardCoins / COINS_PER_USD;

    return transactionDiscounts + referralCoinsValue;
  }, [transactions, rewardCoins]);

  const totalBonusCoins = useMemo(
    () =>
      // Only package bonus coins, NOT referral coins (rewardCoins)
      transactions.reduce(
        (accumulator, transaction) => accumulator + getTransactionBonusCoins(transaction),
        0,
      ),
    [transactions],
  );

  const totalCoinBalance = useMemo(() => {
    const purchasedCoins = Math.round(totalPurchased * COINS_PER_USD);
    return purchasedCoins + totalBonusCoins + rewardCoins;
  }, [rewardCoins, totalBonusCoins, totalPurchased]);

  const totalItoUsdValue = useMemo(
    () => totalCoinBalance * ITO_PRICE_PER_COIN,
    [totalCoinBalance],
  );

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }),
    [],
  );

  const synergyCoinLabel = useMemo(() => {
    // Show "Total Purchased" format: USD ~ purchased coins only (NOT referral coins)
    // Calculate only purchased coins, exclude referral coins
    const purchasedCoins = Math.round(totalPurchased * COINS_PER_USD);
    const formattedCoins = purchasedCoins.toLocaleString();

    return `${formattedCoins}`;
  }, [totalPurchased]);

  const currentValueLabel = useMemo(
    () => currencyFormatter.format(totalPurchased),
    [currencyFormatter, totalPurchased],
  );

  const selectedChartRange = useMemo(() => {
    return (
      CHART_RANGE_OPTIONS.find((option) => option.value === chartRange) ||
      CHART_RANGE_OPTIONS[2]
    );
  }, [chartRange]);

  const chartData = useMemo(() => {
    const range = selectedChartRange;
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    if (!transactions.length) {
      if (range?.days) {
        const buckets = [];
        for (let i = range.days - 1; i >= 0; i -= 1) {
          const date = new Date(now);
          date.setDate(now.getDate() - i);
          buckets.push({
            name: date.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
            key: date.toISOString().slice(0, 10),
            purchased: 0,
            saved: 0,
          });
        }
        return buckets;
      }

      const months = range?.months || 1;
      const buckets = [];
      for (let i = months - 1; i >= 0; i -= 1) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        buckets.push({
          name: date.toLocaleDateString(undefined, { month: "short", year: "numeric" }),
          key: `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}`,
          purchased: 0,
          saved: 0,
        });
      }
      return buckets;
    }

    if (range?.days) {
      const buckets = new Map();
      for (let i = range.days - 1; i >= 0; i -= 1) {
        const date = new Date(now);
        date.setDate(now.getDate() - i);
        const key = date.toISOString().slice(0, 10);
        buckets.set(key, {
          name: date.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
          purchased: 0,
          saved: 0,
        });
      }

      const startDate = new Date(now);
      startDate.setDate(startDate.getDate() - (range.days - 1));

      transactions.forEach((transaction) => {
        if (!transaction.createdAt) return;
        const created = new Date(transaction.createdAt);
        if (Number.isNaN(created.getTime())) return;
        created.setHours(0, 0, 0, 0);
        if (created < startDate) return;

        const key = created.toISOString().slice(0, 10);
        const bucket = buckets.get(key);
        if (!bucket) return;

        bucket.purchased += Number(transaction.amount) || 0;
        bucket.saved += getReferralDiscount(transaction);
      });

      return Array.from(buckets.values());
    }

    const months = range?.months || 1;
    const buckets = new Map();
    for (let i = months - 1; i >= 0; i -= 1) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}`;
      buckets.set(key, {
        name: date.toLocaleDateString(undefined, { month: "short", year: "numeric" }),
        purchased: 0,
        saved: 0,
      });
    }

    const earliestKey = Array.from(buckets.keys())[0];
    const earliestDate = earliestKey
      ? new Date(Number(earliestKey.slice(0, 4)), Number(earliestKey.slice(5, 7)) - 1, 1)
      : null;

    transactions.forEach((transaction) => {
      if (!transaction.createdAt) return;
      const created = new Date(transaction.createdAt);
      if (Number.isNaN(created.getTime())) return;
      if (earliestDate && created < earliestDate) return;

      const key = `${created.getFullYear()}-${(created.getMonth() + 1)
        .toString()
        .padStart(2, "0")}`;
      const bucket = buckets.get(key);
      if (!bucket) return;

      bucket.purchased += Number(transaction.amount) || 0;
      bucket.saved += getReferralDiscount(transaction);
    });

    return Array.from(buckets.values());
  }, [transactions, selectedChartRange]);

  return (
    <DashboardLayout user={user} onSignOut={onSignOut} activeKey="dashboard">
      {({ displayName, avatarUrl, initials, unreadNotifications }) => {
        const greetingName = displayName.split(" ")[0] || "Investor";
        const welcomeMessage =
          user?.welcomeMessage || "Welcome to SynergyCoin.";

        return (
          <>
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between xl:mb-10 2xl:mb-12">
              <div>
                <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl md:text-[52px] md:leading-[1] xl:text-[58px] 2xl:text-[64px]">
                  Hi {greetingName} <span className="inline-block">👋</span>
                </h1>
                <p className="mt-2 text-white/70 xl:text-lg 2xl:text-xl">{welcomeMessage}</p>
              </div>

              {/* Avatar hidden per request */}
            </div>

   <div className="mb-3 text-4xl font-extrabold">Balance</div>



            <div className="relative mb-10 overflow-hidden rounded-2xl border border-white/10 xl:mb-12">
              <div className="relative flex items-center gap-3 overflow-hidden rounded-2xl bg-gradient-to-r from-[#240F35] to-[#07174D] p-4 sm:gap-10 sm:p-6 xl:p-8 2xl:p-10">
                <div className="flex w-full items-stretch gap-3 sm:gap-10">
                  <div className="flex flex-1 min-w-0 basis-0 flex-col justify-center text-left">
                    <div
                      className="px-2 py-1 text-[13px] font-semibold tracking-[0.08em] text-[#ECEFF0] whitespace-nowrap sm:px-3 sm:text-xl"
                      style={{ fontFamily: "Poppins, sans-serif" }}
                    >
                      Synergy Coin
                    </div>


                    <div className="mt-3 whitespace-nowrap text-[14px] font-bold leading-none tracking-tight text-[#CC9EFF] sm:mt-4 sm:text-3xl xl:text-[32px] 2xl:text-[44px]">
                      {synergyCoinLabel}
                    </div>
                  </div>

                  <div className="w-px self-stretch bg-white/15 sm:bg-white/20" />

                  <div className="flex flex-1 min-w-0 basis-0 flex-col justify-center text-right">
                    <div
                      className="px-2 py-1 text-[13px] font-semibold tracking-[0.08em] text-[#ECEFF0] whitespace-nowrap sm:px-3 sm:text-xl"
                      style={{ fontFamily: "Poppins, sans-serif" }}
                    >
                      Current Value
                    </div>

                    <div className="mt-3 whitespace-nowrap text-[14px] font-bold leading-none tracking-tight text-[#CC9EFF] sm:mt-4 sm:text-3xl xl:text-[32px] 2xl:text-[44px]">
                      {currentValueLabel}
                    </div>
                  </div>
                </div>

                <img
                  src="/images/blob-purple.png"
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                  alt=""
                  className="pointer-events-none -mr-2 hidden h-20 w-20 sm:block sm:h-24 sm:w-24 md:h-28 md:w-28 xl:h-32 xl:w-32 2xl:h-36 2xl:w-36"
                />
                <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-white/10" />
              </div>
            </div>

            <div className="mb-6 text-2xl font-extrabold xl:text-3xl 2xl:text-[34px]">Overview</div>

            {statsError && (
              <div className="mb-4 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                {statsError}
              </div>
            )}

            <div className="mb-8 grid grid-cols-3 gap-2 sm:grid-cols-2 sm:gap-4 xl:grid-cols-4 xl:gap-6 2xl:gap-8">
              {[
                {
                  label: "Referral Bonus",
                  value: loadingStats
                    ? "Loading…"
                    : currencyFormatter.format(totalDiscountEarned),
                },
                {
                  label: "Coins Accumulated ",
                  // Show same Synergy Coin amount as top card (purchased coins)
                  value: loadingStats ? "Loading…" : synergyCoinLabel,
                },
                // {
                //   label: "Original Value",
                //   value: loadingStats
                //     ? "Loading…"
                //     : currencyFormatter.format(totalOriginalValue),
                // },
                // {
                //   label: "Packages Owned",
                //   value: loadingStats ? "Loading…" : transactions.length,
                // },
                {
                  label: "ITO Value",
                  value: loadingStats
                    ? "Loading…"
                    : currencyFormatter.format(totalItoUsdValue),
                },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  className="min-w-0 rounded-2xl border border-white/10 bg-black/40 px-2.5 py-3 shadow-[0_16px_40px_rgba(0,0,0,0.35)] sm:px-5 sm:py-4 xl:px-6 xl:py-5 2xl:px-7 2xl:py-6"
                >
                  <p className="text-[11px] font-semibold text-white/60 whitespace-nowrap sm:text-sm xl:text-base">
                    {label}
                  </p>
                  <p
                    className={`mt-2 whitespace-nowrap text-lg font-semibold sm:text-xl xl:text-2xl 2xl:text-[28px] ${
                      label === "Total Purchased"
                        ? "text-[#CC9EFF] tracking-wide"
                        : "text-white"
                    }`}
                  >
                    {value}
                  </p>
                </div>
              ))}
            </div>

            <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#141416] p-5 shadow-[0_8px_30px_rgba(0,0,0,0.4)] xl:p-6 2xl:p-8">
              <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between 2xl:mb-8">
                <div>
                  <div className="text-lg font-semibold xl:text-xl 2xl:text-2xl">Balance Chart</div>
                  <div className="text-sm text-white/50 xl:text-base">
                    Showing purchase volume and referral savings over time
                  </div>
                </div>

                <div className="relative inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/80 xl:px-4 xl:py-2.5 xl:text-base">
                  <CalendarDays size={16} className="text-white/60" />
                  <select
                    aria-label="Select chart range"
                    value={chartRange}
                    onChange={(event) => setChartRange(event.target.value)}
                    className="appearance-none bg-transparent pr-6 text-sm font-medium text-white focus:outline-none xl:text-base"
                  >
                    {CHART_RANGE_OPTIONS.map((option) => (
                      <option
                        key={option.value}
                        value={option.value}
                        className="bg-[#0F0F13] text-white"
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-white/50">
                    ▾
                  </span>
                </div>
              </div>

              <div className="h-[260px] w-full sm:h-[320px] md:h-[360px] xl:h-[420px] 2xl:h-[460px]">
                {loadingStats ? (
                  <div className="flex h-full items-center justify-center text-white/60">
                    Loading chart…
                  </div>
                ) : !transactions.length ? (
                  <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-white/60">
                    <p>No purchases yet.</p>
                    <p className="text-sm text-white/40">
                      When you buy a SynergyCoin package, your progress will appear here.
                    </p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chartData}
                      barCategoryGap={30}
                      margin={{ top: 10, right: 10, left: 36, bottom: 0 }}
                    >
                      <CartesianGrid
                        vertical={false}
                        stroke="#2A2A2A"
                        strokeDasharray="3 3"
                      />
                      <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        stroke="#8E8E93"
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        stroke="#8E8E93"
                        tickFormatter={(value) => currencyFormatter.format(value)}
                      />
                      <Tooltip
                        cursor={{ fill: "rgba(255,255,255,0.04)" }}
                        contentStyle={{
                          background: "#1A1A1C",
                          border: "1px solid rgba(255,255,255,0.08)",
                          borderRadius: "12px",
                          color: "#fff",
                          fontSize: 12,
                        }}
                        formatter={(value, key) => [
                          currencyFormatter.format(value),
                          key === "purchased" ? "Purchased" : "Referral Savings",
                        ]}
                      />
                      <Bar
                        dataKey="purchased"
                        radius={[6, 6, 0, 0]}
                        fill="#8B5CF6"
                        maxBarSize={55}
                      />
                      <Bar
                        dataKey="saved"
                        radius={[6, 6, 0, 0]}
                        fill="#D6BCFA"
                        maxBarSize={55}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
              <div className="pb-24 sm:pb-0"></div>

          </>
        );
      }}
    </DashboardLayout>
    
  );
}
